/**
 * useBulkEditorSession — Campaign Bulk Editor session for the CLW review grid
 * and the Copy Campaign Adjust grid.
 *
 * Thin adapter over useReviewerSession (the shared reviewer transport), plus the
 * two actions that are specific to the bulk-edit protocol and have no analogue in
 * the reviewer hooks:
 *
 *   approvePreview(previewId)  — THE CONFIRM GATE. Posts the user's explicit
 *     approval so the agent's next confirm_and_stage succeeds. The agent cannot
 *     do this for itself; nothing stages without it.
 *   refreshDigest({ rows, … })  — re-seed the agent's row view after the grid
 *     changes, which also invalidates any outstanding preview (row ids are
 *     positional on GroundTruth and Google Ads, so a stale preview can point at
 *     the wrong rows).
 *
 * Platform state:
 *   activePreview   — the proposal awaiting confirmation. One at a time: a new
 *                     proposal replaces the card, which is also the UX we want.
 *   lastStagedBatch — { staging, summary, … } for the host to stage into the
 *                     grid's pending-edit state.
 *   lastBulkFailure — a refused / failed propose or stage, for the banner.
 *
 * Unlike the reviewer hooks this one is platform-agnostic: `platform` and
 * `surface` ride in the create payload rather than picking a route.
 *
 * ── GroundTruth chat (platform === 'groundtruth' only) ─────────────────────
 * On GroundTruth the hook additionally keeps the chat's CARDS (see
 * agent/gtChatState.js):
 *
 *   proposals     every preview, kept after it is confirmed / cancelled /
 *                 replaced / undone, with its status. activePreview still
 *                 mirrors the one live pending proposal.
 *   choiceSets    the agent's structured clarification questions
 *                 (present_choices), answered by click or typed reply.
 *   choiceNotices local "that didn't pick an option" notes.
 *
 * and three actions: choose() answers the latest open choice card,
 * reportProposalOutcome() records the surface's save / undo result, and the
 * GT sendMessage routes a typed reply to the choice card first when one is the
 * live question — so a typed "2" picks option 2, a typed "7" redisplays the
 * choices, and a typed "yes" can never approve a preview the user is not
 * looking at. Every other platform keeps its exact previous behaviour.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';
import { useReviewerSession } from './useReviewerSession';
import { rowsFingerprint, isPlainApproval } from '../components/CampaignLaunchWorkflow/agent/bulkIntent';
import { itemReadyToShow, previewReadyToShow } from '../components/CampaignLaunchWorkflow/agent/previewGate';
import { resolveChoiceReply } from '../components/CampaignLaunchWorkflow/agent/choiceResolver';
import {
  GT_CHAT_INITIAL,
  addChoiceSet,
  addProposal,
  closeOpenChoiceSets,
  expireProposals,
  findProposal,
  latestOpenChoiceSet,
  latestPendingProposal,
  resolveChoiceSet,
  restoreChatHistory,
  setProposalStatus,
} from '../components/CampaignLaunchWorkflow/agent/gtChatState';

const ROUTE_BASE = '/api/v2/bulk-editor-sessions';
// Must match services/bulkEdit/fieldMap.js#PLATFORMS. Facebook rides its
// canonical spelling 'facebook' (not the launcherFamily short form 'fb') —
// that is what the agent_sessions platform CHECK constraint permits.
const PLATFORMS = ['dv360', 'ttd', 'gads', 'facebook', 'groundtruth', 'yahoo'];

/** How a clicked pick reads in the transcript. */
const choiceLabelText = (choice) => (choice.id === 'yes' || choice.id === 'no'
  ? choice.label
  : `${choice.number}. ${choice.label}`);

export function useBulkEditorSession({ accountId, userId, autoStart = false, createPayload }) {
  // The GroundTruth chat experience (structured choice cards + preserved
  // proposal cards) is keyed on the session's platform and nothing else.
  const gtChat = createPayload?.platform === 'groundtruth';
  const session = useReviewerSession({
    accountId,
    userId,
    autoStart,
    createPayload,
    routeBase: ROUTE_BASE,
    logName: 'useBulkEditorSession',
    validateCreatePayload: (p) => {
      if (!p?.platform || !PLATFORMS.includes(p.platform)) {
        return `Missing or unsupported platform (expected one of: ${PLATFORMS.join(', ')})`;
      }
      if (!p?.campaignId) return 'Missing campaignId';
      if (!Array.isArray(p?.rows) || p.rows.length === 0) return 'No plan rows to edit yet';
      return null;
    },
    isAutoStartReady: (p) => Boolean(
      p?.platform && PLATFORMS.includes(p.platform) && p?.campaignId
      && Array.isArray(p?.rows) && p.rows.length > 0,
    ),
    // Row identity + count is what matters for a restart: a different plan, or a
    // plan that just finished loading, should start a session. Row CONTENT
    // changes go through refreshDigest instead of tearing the session down.
    autoStartDeps: [
      createPayload?.platform,
      createPayload?.campaignId,
      createPayload?.rows?.length,
    ],
    initialPlatformState: {
      activePreview: null,
      lastStagedBatch: null,
      lastBulkFailure: null,
      // GroundTruth chat cards — stay empty on every other platform.
      ...GT_CHAT_INITIAL,
    },
    // A resume can hand back the newest still-pending preview — restore its
    // Confirm card so a reload doesn't lose an un-confirmed proposal.
    reduceCreateData: (data) => {
      const pending = data?.pendingPreview
        ? { activePreview: { ...data.pendingPreview, ts: Date.now() } }
        : null;
      if (!gtChat) return pending;
      const restored = restoreChatHistory(data?.chatHistory)
        || (pending ? { proposals: [], choiceSets: [], choiceNotices: [] } : null);
      if (!restored) return pending;
      // Read-only unless it is THE live pending preview the backend handed
      // back: history alone never recreates a Confirm button.
      const live = data?.pendingPreview || null;
      const liveId = live?.preview_id ? String(live.preview_id) : null;
      restored.proposals = restored.proposals.map((p) => (
        p.status === 'pending' && p.previewId !== liveId
          ? { ...p, status: 'expired', detail: p.detail || 'This proposal is no longer available.' }
          : p
      ));
      // A live preview with no history entry (a session that predates the
      // history, resumed across the deploy) still gets its card.
      if (liveId && !restored.proposals.some((p) => p.previewId === liveId)) {
        restored.proposals.push({
          previewId: liveId,
          preview: live,
          status: 'pending',
          ts: Number.isFinite(live.created_at) ? live.created_at : null,
          decidedAt: null,
          detail: null,
          restored: true,
        });
      }
      return { ...(pending || { activePreview: null }), ...restored };
    },
    reducePlatformEvent: (data) => {
      if (data.type === 'bulk.preview' && data.preview) {
        const ts = Date.now();
        const patch = { activePreview: { ...data.preview, ts }, lastBulkFailure: null };
        if (!gtChat) return patch;
        return (prev) => ({ ...patch, ...addProposal(prev, data.preview, ts) });
      }
      if (data.type === 'bulk.choices' && data.choice_set) {
        if (!gtChat) return null;
        const ts = Date.now();
        return (prev) => addChoiceSet(prev, data.choice_set, ts);
      }
      if (data.type === 'bulk.staged') {
        const patch = {
          lastStagedBatch: {
            preview_id: data.preview_id,
            staging: data.staging || { edits: {}, listOps: {}, rowOps: [] },
            summary: data.summary || null,
            staged_count: data.staged_count || 0,
            rows_affected: data.rows_affected || 0,
            ts: Date.now(),
          },
          // The card's job is done — clear it so a stale Confirm button can't
          // linger next to an already-staged batch.
          activePreview: null,
        };
        if (!gtChat) return patch;
        return (prev) => {
          const card = data.preview_id ? findProposal(prev, data.preview_id) : null;
          // Cancelled only on THIS side (the Cancel is still in flight): the
          // server staged the batch before it saw the Cancel, and will refuse
          // the Cancel. The server is the authority — the batch reaches the grid
          // and the card says what happened.
          if (card?.status === 'cancelled') {
            return {
              ...patch,
              proposals: prev.proposals.map((p) => (p.previewId === card.previewId
                ? { ...p, status: 'staged', detail: 'Applied before the cancel reached the server.' }
                : p)),
            };
          }
          // Never hand the grid the same preview twice: a card already past
          // "applying" has had its batch.
          if (card && card.status !== 'pending' && card.status !== 'applying') {
            return { activePreview: null };
          }
          return { ...patch, ...setProposalStatus(prev, data.preview_id, 'staged') };
        };
      }
      if (data.type === 'bulk.failed') {
        const patch = {
          lastBulkFailure: {
            tool: data.tool || null,
            error: data.error || 'The bulk operation failed.',
            awaitingApproval: Boolean(data.awaiting_approval),
            ts: Date.now(),
          },
        };
        if (!gtChat) return patch;
        return (prev) => {
          // A confirmed card whose stage failed says so instead of spinning.
          const card = data.tool === 'confirm_and_stage' && data.preview_id && !data.awaiting_approval
            ? findProposal(prev, data.preview_id) : null;
          if (card?.status !== 'applying') return patch;
          return {
            ...patch,
            ...setProposalStatus(prev, card.previewId, 'save_failed', { detail: patch.lastBulkFailure.error }),
          };
        };
      }
      return null;
    },
  });

  const { sessionId } = session;
  // The latest session, read by the GroundTruth paths at CALL time. Keeps the
  // callbacks' identities as stable as they were before (the non-GT paths close
  // over exactly what they always did) while the GT paths still see current
  // cards.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);
  // GroundTruth: a click and a typed "yes" can land in the same tick; the ref
  // makes the second one a no-op rather than a second approval + nudge.
  const approvingRef = useRef(false);
  const [choosing, setChoosing] = useState(null); // choice_set_id in flight
  const choosingRef = useRef(false);
  const [choiceError, setChoiceError] = useState(null);

  // GroundTruth: an error belongs to the card it came from. When a NEW live
  // proposal or a NEW question appears, the previous one's error goes (and not
  // when a card merely closes — the refusal that closed it stays readable).
  // "New" means not the last one seen: a card that was put back (a Cancel the
  // server refused) is the same card, and keeps its error.
  const liveProposalId = gtChat ? (latestPendingProposal(session)?.previewId || null) : null;
  const openChoiceSetId = gtChat ? (latestOpenChoiceSet(session)?.choice_set_id || null) : null;
  const lastLiveProposal = useRef(null);
  const lastOpenChoiceSet = useRef(null);
  useEffect(() => {
    if (!gtChat || !liveProposalId || liveProposalId === lastLiveProposal.current) return;
    lastLiveProposal.current = liveProposalId;
    setApproveError(null);
  }, [gtChat, liveProposalId]);
  useEffect(() => {
    if (!gtChat || !openChoiceSetId || openChoiceSetId === lastOpenChoiceSet.current) return;
    lastOpenChoiceSet.current = openChoiceSetId;
    setChoiceError(null);
  }, [gtChat, openChoiceSetId]);

  /**
   * GroundTruth: the server refused an approval — its word on the card wins,
   * whether the approval came from the button or a typed "yes" (AC8): a
   * replaced, cancelled or vanished preview turns read-only instead of keeping
   * a Confirm button nothing can honour.
   */
  const markRefusedApproval = useCallback((previewId, err) => {
    const data = err?.response?.data || {};
    const message = data.error || err?.message || null;
    const status = data.superseded ? 'superseded'
      : (data.cancelled ? 'cancelled' : (err?.response?.status === 404 ? 'expired' : null));
    if (!status) return;
    sessionRef.current.updatePlatformState((prev) => ({
      ...setProposalStatus(prev, previewId, status, { detail: message }),
      ...(prev.activePreview?.preview_id === previewId ? { activePreview: null } : {}),
    }));
  }, []);

  /**
   * GroundTruth: answer the LATEST open choice card. By click, or by a typed
   * reply the resolver matched to one option. The pick goes to /choose — never
   * to approve-preview — so it cannot confirm anything; the agent turns it into
   * a new preview the user still has to confirm. The card is marked answered,
   * and the pick shown in the transcript, only once it reached the agent.
   */
  const choose = useCallback(async (choiceSetId, choiceId, via = 'click', typedText = null, idOverride = null) => {
    const current = sessionRef.current;
    const sid = idOverride || current.sessionId;
    if (!gtChat || !sid) return false;
    const open = latestOpenChoiceSet(current);
    if (!open || open.choice_set_id !== choiceSetId) {
      setChoiceError('That question was replaced. Answer the latest one.');
      return false;
    }
    const choice = (open.choices || []).find((c) => String(c.id) === String(choiceId));
    if (!choice) return false;
    if (choosingRef.current) return false;
    choosingRef.current = true;
    setChoosing(choiceSetId);
    setChoiceError(null);
    const shown = (via === 'typed' && typedText && typedText.trim()) || choiceLabelText(choice);
    try {
      await api.post(getApiUrl(`${ROUTE_BASE}/${sid}/choose`), {
        choiceSetId, choiceId: choice.id, via, text: shown, accountId, userId,
      });
      const at = Date.now();
      current.updatePlatformState((prev) => resolveChoiceSet(prev, choiceSetId, choice, via, at));
      // One ms after the card's decidedAt, so the card sorts above the pick.
      current.pushLocalMessage({ role: 'user', text: shown, ts: at + 1 });
      return true;
    } catch (err) {
      const data = err?.response?.data || {};
      // Only a server that has MOVED ON (a newer question, an answered or
      // unknown one) closes the card. A transient refusal — the agent is
      // mid-tool-call (409 pendingTool), a 502 — leaves it open, with the error
      // on it, so the user can simply pick again.
      if (data.code === 'stale' || data.code === 'not_found' || err?.response?.status === 404) {
        current.updatePlatformState((prev) => ({ choiceSets: closeOpenChoiceSets(prev.choiceSets) }));
      }
      setChoiceError(data.error || err.message);
      return false;
    } finally {
      choosingRef.current = false;
      setChoosing(null);
    }
  }, [gtChat, accountId, userId]);

  // A typed plain "yes" IS the user's explicit approval of the visible
  // preview card — grant the approval marker deterministically before the
  // message reaches the agent, so its confirm_and_stage succeeds instead of
  // bouncing "didn't register as approved" in an endless re-ask loop
  // (UAT 0819). nudge:false — the user's own message drives the agent's turn.
  // Qualified replies ("yes but move the dates") don't match the classifier
  // and ride through as ordinary conversation.
  const sendMessage = useCallback(async (text, idOverride = null) => {
    if (gtChat) {
      // ── GroundTruth ────────────────────────────────────────────────────
      const current = sessionRef.current;
      const open = latestOpenChoiceSet(current);
      const pendingCard = latestPendingProposal(current);
      // An open choice card is ALWAYS the live question: a new proposal closes
      // open questions (here and on the server), so an open card is newer than
      // any pending proposal by construction — no clock comparison needed, which
      // matters because restored cards carry server time and live ones client
      // time.
      // …once it is actually on screen: a card the feed is still holding back
      // (the agent is finishing its turn) cannot be answered by a reply the
      // user typed without seeing it. That reply goes as ordinary text.
      if (open && (open.restored || itemReadyToShow(current, open.ts))) {
        // A pick is already on its way: say so rather than drop this one, or
        // send it alongside and contradict that pick.
        if (choosingRef.current) {
          const msg = current.pushLocalMessage({ role: 'user', text, local: true });
          current.updatePlatformState((prev) => ({
            choiceNotices: [...(prev.choiceNotices || []), {
              key: `choice-notice-${msg.ts}`,
              ts: msg.ts + 1,
              choiceSetId: open.choice_set_id,
              reason: 'Your pick is still being sent — send this again once it has gone through.',
            }],
          }));
          return undefined;
        }
        const reply = resolveChoiceReply(text, open);
        if (reply.kind === 'selected') {
          return choose(open.choice_set_id, reply.choice.id, 'typed', text, idOverride);
        }
        if (reply.kind === 'invalid' || reply.kind === 'ambiguous') {
          // Nothing is sent — the reply cannot be resolved to one option, and a
          // guess must not become an edit. Show what they typed and redisplay
          // the choices under it.
          const msg = current.pushLocalMessage({ role: 'user', text, local: true });
          current.updatePlatformState((prev) => ({
            choiceNotices: [...(prev.choiceNotices || []), {
              key: `choice-notice-${msg.ts}`,
              ts: msg.ts + 1,
              choiceSetId: open.choice_set_id,
              reason: reply.reason,
            }],
          }));
          return undefined;
        }
        // Qualified or unrelated: a NEW request, sent as ordinary text. The
        // backend closes the card; mirror that now so it can't be clicked
        // while the message is in flight.
        current.updatePlatformState((prev) => ({ choiceSets: closeOpenChoiceSets(prev.choiceSets) }));
        return current.sendMessage(text, idOverride);
      }
      // A question still held back goes the way the server takes it: this reply
      // is free-form, so it closes that question there — mirror it here.
      if (open) {
        current.updatePlatformState((prev) => ({ choiceSets: closeOpenChoiceSets(prev.choiceSets) }));
      }
      // Typed approval applies ONLY to the latest pending proposal, only once
      // its card is actually on screen, and never past a blocker the Confirm
      // button would refuse.
      if (
        pendingCard && isPlainApproval(text) && !approvingRef.current
        && !(pendingCard.preview?.blockers?.length)
        && current.activePreview?.preview_id === pendingCard.previewId
        && previewReadyToShow(current)
      ) {
        approvingRef.current = true;
        setApproving(true);
        setApproveError(null);
        try {
          await api.post(getApiUrl(`${ROUTE_BASE}/${idOverride || current.sessionId}/approve-preview`), {
            previewId: pendingCard.previewId, via: 'typed', nudge: false, accountId, userId,
          });
          current.updatePlatformState((prev) => setProposalStatus(prev, pendingCard.previewId, 'applying'));
        } catch (err) {
          console.warn('[useBulkEditorSession] typed approval failed:', err?.response?.data?.error || err.message);
          setApproveError(err?.response?.data?.error || err.message);
          markRefusedApproval(pendingCard.previewId, err);
        } finally {
          approvingRef.current = false;
          setApproving(false);
        }
      }
      return current.sendMessage(text, idOverride);
    }

    const pv = session.activePreview;
    if (pv?.preview_id && isPlainApproval(text)) {
      try {
        await api.post(getApiUrl(`${ROUTE_BASE}/${idOverride || session.sessionId}/approve-preview`), {
          previewId: pv.preview_id, via: 'typed', nudge: false, accountId, userId,
        });
      } catch (err) {
        // A 404 here means the preview is gone (restart) — the agent will say
        // so and re-propose; the message still goes through either way.
        console.warn('[useBulkEditorSession] typed approval failed:', err?.response?.data?.error || err.message);
      }
    }
    return session.sendMessage(text, idOverride);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtChat, session.activePreview, session.sessionId, session.sendMessage, choose, markRefusedApproval, accountId, userId]);

  /**
   * "New session" clears the conversation. On GroundTruth the cards are part of
   * that conversation (and belong to a session that is being terminated, so none
   * could be confirmed or answered any more) — they go with it.
   */
  const newSession = useCallback(async () => {
    const current = sessionRef.current;
    // Every platform: the pending card belongs to the session being terminated —
    // left on screen, its Confirm / Cancel would aim at a preview the new
    // session has never seen.
    current.updatePlatformState(gtChat
      ? { ...GT_CHAT_INITIAL, activePreview: null, lastBulkFailure: null }
      : { activePreview: null, lastBulkFailure: null });
    return current.newSession();
  }, [gtChat]);

  /**
   * The agent's OWN row ops are not a stale-context event.
   *
   * The guard below resets the session when the set of rows changes underneath the
   * agent. That is right for a change it did not make — a manual row delete, a
   * fresh plan load — but wrong for one it just staged itself: it proposed the op,
   * the user confirmed it, and it already knows. Since the surfaces auto-save an
   * approved batch, a confirmed add/delete now lands in the grid within a second
   * or two, and without this the agent's conversation was wiped immediately after
   * it did what it was asked — mid-explanation, on the very turn it reported
   * success.
   *
   * So a staged batch carrying rowOps absorbs exactly the next fingerprint change.
   * refresh-digest still re-seeds the backend rows, which is what actually keeps
   * the agent current.
   */
  const absorbNextRowChange = useRef(false);
  const seenStagedTs = useRef(null);
  const stagedBatch = session.lastStagedBatch;
  useEffect(() => {
    if (!stagedBatch || stagedBatch.ts === seenStagedTs.current) return;
    seenStagedTs.current = stagedBatch.ts;
    if ((stagedBatch.staging?.rowOps || []).length > 0) absorbNextRowChange.current = true;
  }, [stagedBatch]);

  /**
   * The same exemption for the surface's own Undo of a row-op batch: the row
   * set changes back, and the conversation — with the card that is about to say
   * "Undone" — must survive it. The host arms it just before reverting and
   * disarms it if the revert did not happen.
   */
  const expectRowChange = useCallback(() => { absorbNextRowChange.current = true; }, []);
  const cancelExpectedRowChange = useCallback(() => { absorbNextRowChange.current = false; }, []);

  /**
   * The plan changing under the agent is the other way its context goes stale —
   * and the cause of the "I count 5 ad groups, not 4" argument, since
   * refresh-digest updates the backend rows silently and the agent keeps quoting
   * its first digest read. Row IDENTITY only, so editing a cell does not reset a
   * live conversation while adding or deleting a row does.
   */
  const rowsPrint = rowsFingerprint(createPayload?.rows);
  const lastRowsPrint = useRef(null);
  useEffect(() => {
    if (!session.sessionId || !rowsPrint) return;
    if (lastRowsPrint.current === null) { lastRowsPrint.current = rowsPrint; return; }
    if (lastRowsPrint.current === rowsPrint) return;
    lastRowsPrint.current = rowsPrint;
    // The agent caused this one — see absorbNextRowChange above.
    if (absorbNextRowChange.current) { absorbNextRowChange.current = false; return; }
    // Nothing said yet — the pending auto-start/first read will see fresh rows.
    if ((session.messages?.length || 0) === 0) return;
    newSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPrint, session.sessionId]);

  /**
   * Record the user's explicit approval of `previewId`. `via` is 'click' when
   * they pressed Confirm and 'typed' when they typed an unambiguous approval.
   */
  const approvePreview = useCallback(async (previewId, via = 'click') => {
    if (!sessionId || !previewId) return false;
    if (gtChat) {
      // Only the latest pending proposal can be confirmed, and only once: a
      // repeat click, or one aimed at a replaced / cancelled / historical card,
      // is refused here before it reaches the gate.
      const current = sessionRef.current;
      const card = findProposal(current, previewId);
      const live = latestPendingProposal(current);
      if (card && (card.status !== 'pending' || live?.previewId !== card.previewId)) return false;
      if (approvingRef.current) return false;
      approvingRef.current = true;
    }
    setApproving(true);
    setApproveError(null);
    try {
      const res = await api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/approve-preview`), {
        previewId, via, accountId, userId,
      });
      if (gtChat) {
        // The approval landed but the agent was not told (nudge failed): say
        // so on the card rather than let "Applying…" spin with no way forward.
        const stalled = res?.data?.nudged === false && res?.data?.warning;
        sessionRef.current.updatePlatformState((prev) => setProposalStatus(prev, previewId, 'applying', {
          detail: stalled
            ? 'The agent has not picked this up yet — reply "go ahead" to have it apply the confirmed change.'
            : null,
        }));
      }
      return true;
    } catch (err) {
      // The commonest cause is a preview that no longer exists (backend restart,
      // or a digest refresh invalidated it). Surface it rather than leaving the
      // button spinning — the fix is to ask the agent to re-propose.
      const message = err?.response?.data?.error || err.message;
      setApproveError(message);
      if (gtChat) markRefusedApproval(previewId, err);
      return false;
    } finally {
      if (gtChat) approvingRef.current = false;
      setApproving(false);
    }
  }, [sessionId, accountId, userId, gtChat, markRefusedApproval]);

  /**
   * The user rejected a proposal. Marks the preview dismissed server-side so
   * a session resume can never resurrect its Confirm card (the conversational
   * "don't stage it" message alone left the preview looking pending).
   *
   * On GroundTruth the backend also tells the agent (notify) — the host does
   * not send a chat message for it, which would close an open choice card —
   * and revokes an approval that has not been staged yet, so the Cancel wins.
   * The card goes to Cancelled at once and is put back if the server says the
   * proposal was no longer cancellable (already applied, or replaced).
   */
  const dismissPreview = useCallback(async (previewId) => {
    if (!sessionId || !previewId) return;
    let prior = null;
    let priorActive = null;
    if (!gtChat) {
      // The card goes at once. Nothing else would take it down: no event follows
      // a dismissal, so Cancel used to leave the card sitting there.
      sessionRef.current.updatePlatformState((prev) => (
        prev.activePreview?.preview_id === previewId ? { activePreview: null } : null
      ));
    }
    if (gtChat) {
      // Never race an in-flight Confirm (the button is disabled for it too).
      if (approvingRef.current) return;
      const current = sessionRef.current;
      prior = findProposal(current, previewId);
      priorActive = current.activePreview;
      // The card stays, marked Cancelled and read-only; typed approval can no
      // longer reach it because it is no longer the active preview.
      current.updatePlatformState((prev) => ({
        ...setProposalStatus(prev, previewId, 'cancelled'),
        ...(prev.activePreview?.preview_id === previewId ? { activePreview: null } : {}),
      }));
    }
    const putBack = () => {
      if (!prior) return;
      sessionRef.current.updatePlatformState((prev) => {
        // Only a card that still reads Cancelled goes back — one that moved on
        // meanwhile (its batch was staged) keeps what the server said.
        const card = findProposal(prev, prior.previewId);
        if (card?.status !== 'cancelled') return null;
        return {
          proposals: prev.proposals.map((p) => (p.previewId === prior.previewId
            ? { ...p, status: prior.status, decidedAt: prior.decidedAt, detail: prior.detail }
            : p)),
          ...(priorActive?.preview_id === previewId && !prev.activePreview ? { activePreview: priorActive } : {}),
        };
      });
    };
    try {
      const res = await api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/dismiss-preview`), {
        previewId, accountId, userId, ...(gtChat ? { notify: true } : {}),
      });
      if (gtChat && res?.data?.dismissed === false) putBack();
    } catch (err) {
      console.warn('[useBulkEditorSession] dismiss-preview failed:', err.message);
      if (gtChat) {
        putBack();
        setApproveError(`Couldn't cancel that proposal — ${err?.response?.data?.error || err.message}`);
      }
    }
  }, [sessionId, accountId, userId, gtChat]);

  /**
   * GroundTruth: record what became of a confirmed proposal — the surface owns
   * the auto-save and the Undo, so it reports 'saved' / 'save_failed' /
   * 'undone' here. Updates the card immediately and persists the status so a
   * reload shows it. A no-op on other platforms.
   */
  const reportProposalOutcome = useCallback(async (previewId, status, detail = null) => {
    if (!gtChat || !previewId || !status) return;
    sessionRef.current.updatePlatformState((prev) => setProposalStatus(prev, previewId, status, { detail }));
    if (!sessionId) return;
    try {
      await api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/proposal-status`), {
        previewId, status, ...(detail ? { detail } : {}), accountId, userId,
      });
    } catch (err) {
      console.warn('[useBulkEditorSession] proposal-status failed:', err?.response?.data?.error || err.message);
    }
  }, [gtChat, sessionId, accountId, userId]);

  /**
   * Push the current grid rows to the agent. Call after a save, a manual edit
   * batch, or any row add/delete so the agent stops reasoning about stale rows.
   */
  const refreshDigest = useCallback(async (payload) => {
    if (!sessionId || !payload?.rows) return false;
    try {
      const res = await api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/refresh-digest`), {
        ...payload, accountId, userId,
      });
      // GroundTruth: the row set changed under undecided proposals, so the
      // server dropped them — their cards must not keep a live Confirm.
      // Exactly the cards it names — a bare count would also expire a card the
      // server staged, or proposed, while this request was in flight.
      const expiredIds = res?.data?.expired_previews;
      if (gtChat && Array.isArray(expiredIds) && expiredIds.length) {
        sessionRef.current.updatePlatformState((prev) => expireProposals(prev, expiredIds, 'The plan changed before this was confirmed.'));
      }
      return true;
    } catch (err) {
      console.warn('[useBulkEditorSession] refresh-digest failed:', err.message);
      return false;
    }
  }, [sessionId, accountId, userId, gtChat]);

  return {
    ...session,
    // AFTER the spread: replaces the transport's sendMessage so every consumer
    // (the sidebar's input, the Cancel-preview message) gets the typed-approval
    // behaviour without having to know about it.
    sendMessage,
    // Same reason: "New session" also clears the pending card (and on
    // GroundTruth, every card).
    newSession,
    approvePreview,
    dismissPreview,
    refreshDigest,
    approving,
    approveError,
    clearApproveError: () => setApproveError(null),
    // GroundTruth chat (inert elsewhere).
    gtChat,
    choose,
    choosing,
    choiceError,
    clearChoiceError: () => setChoiceError(null),
    reportProposalOutcome,
    expectRowChange,
    cancelExpectedRowChange,
  };
}

export default useBulkEditorSession;
